import { updateContent } from 'core/crud/contents.crud';
import { useLemonadeEditorViewer } from 'core/modules/components/lemonade/hook/useLemonadeEditorViewer';
import * as coreEntities from 'core/store/index';
import { LEMONADE_QUESTION_TYPES, parseTemplateIdForLemonade } from 'core/utils/lemonade';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';

const useDialogLemonadeEditor = ({ selectedTemplate, onSubmit, item, uploadURL, newActivityContent, onClickFeedbackAi, hasRequiredTitle }) => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const isPowerLessonMode = useSelector((state) => coreEntities.history.selectors.getIsPowerLessonMode(state));
  const shouldUseItemData = !selectedTemplate || selectedTemplate === LEMONADE_QUESTION_TYPES.SINGLE_CHOICE_AI || (hasRequiredTitle && item?.data?.title?.trim());
  const { Question, Editor, editorData } = useLemonadeEditorViewer(
    shouldUseItemData
      ? { data: item?.data, uploadURL, onClickFeedbackAi }
      : {
          defaultData: parseTemplateIdForLemonade(selectedTemplate),
          uploadURL,
          onClickFeedbackAi,
        }
  );
  const [data, setData] = useState(item?.data || {});
  const [isUpdating, setIsUpdating] = useState(true);
  const isTitleEmpty = hasRequiredTitle && (!data?.title || data?.title?.trim() === '');
  const [currentContent, setCurrentContent] = useState(item ? { ...item.data, guid: item.data.content_guid || item.data.guid } : newActivityContent);
  const [background, setBackground] = useState(null);

  useEffect(() => {
    setCurrentContent(item ? { ...item.data, guid: item.data.content_guid || item.data.guid } : newActivityContent);
  }, []);

  useEffect(() => {
    if (data?.data?.ui_style?.background) {
      setBackground(data.data.ui_style.background);
    }
  }, [data]);

  useEffect(() => {
    if (selectedTemplate === LEMONADE_QUESTION_TYPES.SINGLE_CHOICE_AI) setIsUpdating(false);
  }, [selectedTemplate]);

  useEffect(() => {
    if (!isEmpty(editorData)) {
      setIsUpdating(false);
    }
    if (editorData?.store) setData(editorData.store.output);
    if (editorData?.reference) setData(editorData);
  }, [editorData]);

  async function onSaveDataLemonade() {
    try {
      await updateContent({
        guid: currentContent.guid,
        ...(hasRequiredTitle && { name: data?.title }),
        data: { ...data, ...data.data, reference: currentContent.guid },
      });

      onSubmit(currentContent, data);
    } catch (e) {
      // Surface the failure instead of swallowing it: without this the save silently
      // failed and the editor stayed open with no feedback (TAN-10124).
      console.error('Error saving Lemonade activity', e);
      addToast(t('library:Error saving changes'), { appearance: 'error', autoDismiss: true });
    }
  }

  return { Question, Editor, data, isUpdating, isTitleEmpty, onSaveDataLemonade, background, isPowerLessonMode };
};

export default useDialogLemonadeEditor;
